<template>
  <el-form :model="data" class="activity-review">
    <div class="activity-review__info">
      <review-info-box
          :company="data.customerCompanyName"
          :po="data.po"
          :partNumber="data.partNumber"
          :partSN="data.partSN"
          :baxPO="data.baxpo"
          :rfq-type="dataSourceRfqType.map[data.rfqTypeID] && dataSourceRfqType.map[data.rfqTypeID].title"
          :supplierCompanyName="data.supplierCompanyName"
      />
    </div>
    <div class="activity-review__boxes">
      <div class="row mx-0">
        <div class="col-xl-2 col-md-4 col-6">
          <div class="activity-review__box" :class="classShipped">
            <el-form-item
              prop="shipped"
              class="material-style mr-1"
            >
              <el-checkbox
                v-model="form.shipped"
                @change="autoSave('shipped',  { payloadData: { shippedDate: new Date() }})"
              />
            </el-form-item>
            <el-form-item
              prop="shippedDate"
              :label="L10n('shipped-date')"
              class="material-style"
            >
              <date-picker
                v-model="form.shippedDate"
                @change="autoSave('shippedDate')"
              >
              </date-picker>
            </el-form-item>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-6">
          <div class="activity-review__box" :class="classQuoteReceived">
            <el-form-item
              prop="quoteReceived"
              class="material-style mr-1"
            >
              <el-checkbox
                v-model="form.quoteReceived"
                @change="autoSave('quoteReceived', { payloadData: { quoteReceivedDate: new Date() }})"
              />
            </el-form-item>
            <el-form-item
              :label="L10n('quote-received')"
              prop="quoteReceivedDate"
              class="material-style"
            >
              <date-picker
                v-model="form.quoteReceivedDate"
                @change="autoSave('quoteReceivedDate')"
              >
              </date-picker>
            </el-form-item>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-6">
          <div class="activity-review__box" :class="classQuoteApproved">
            <el-form-item
              prop="quoteApproved"
              class="material-style mr-1"
            >
              <el-checkbox
                v-model="form.quoteApproved"
                @change="autoSave('quoteApproved', { payloadData: { quoteApprovedDate: new Date() }})"
              />
            </el-form-item>
            <el-form-item
              :label="L10n('quote-approved')"
              prop="quoteApprovedDate"
              class="material-style"
            >
              <date-picker
                v-model="form.quoteApprovedDate"
                @change="autoSave('quoteApprovedDate')"
              >
              </date-picker>
            </el-form-item>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-6">
          <div class="activity-review__box" :class="classUnitShipped">
            <el-form-item
              prop="unitShipped"
              class="material-style mr-1"
            >
              <el-checkbox
                v-model="form.unitShipped"
                @change="autoSave('unitShipped', { payloadData: { unitShippedDate: new Date() }})"
              />
            </el-form-item>
            <el-form-item
              :label="L10n('ship-rcvd-by-bax')"
              prop="unitShippedDate"
              class="material-style"
            >
              <date-picker
                v-model="form.unitShippedDate"
                @change="autoSave('unitShippedDate')"
              >
              </date-picker>
            </el-form-item>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-6">
          <div class="activity-review__box">
            <el-form-item
              :label="L10n('order-phase')"
              prop="orderPhaseID"
              class="material-style mr-1"
              :class="orderPhaseClass"
            >
              <select-response
                :model.sync="form.orderPhaseID"
                :isLoading="dataSourceOrderPhase.loading"
                :data="dataSourceOrderPhase.records"
                @on-change="(id, data) => autoSave('orderPhaseID', { label: data && data.title, payloadData: { phaseDate: new Date() }})"
              >
              </select-response>
            </el-form-item>
            <el-form-item
              :label="L10n('phase-date')"
              prop="phaseDate"
              class="material-style"
            >
              <date-picker
                v-model="form.phaseDate"
                @change="autoSave('phaseDate')"
              >
              </date-picker>
            </el-form-item>
          </div>
        </div>
        <div class="col-xl-2 col-md-4 col-6">
          <div class="activity-review__box" :class="classConfirmedEdd" style="padding-left: 1.8rem">
            <el-form-item
              :label="L10n('confirmed-edd')"
              class="material-style"
            >
              <date-picker
                v-model="form.estimatedDeliveryDate"
                @change="autoSave('estimatedDeliveryDate', { api: `RFQ2s/Patch/${form.rfq2ID}` })"
              >
              </date-picker>
            </el-form-item>
          </div>
        </div>
      </div>
    </div>
    <div class="activity-review__control">
      <ui-button
        style="height: 100%"
        class="btn btn-size-s"
        :label="L10n('details')"
        isVertical
        @click="$emit('onDetails')"
      />
    </div>
  </el-form>
</template>

<script>
import Form from '@/components/Form/Form'

export default {
  name: 'ActivityReviewForm',
  mixins: [Form],
  components: {
    'review-info-box': () => import(/* webpackChunkName: 'Shared' */ '@/components/Shared/Activity/ReviewInfoBox')
  },
  computed: {
    dataSourceOrderPhase () {
      return this.API('OrderPhases/GetAllDynamic?select=id,title&filter=!title.startsWith("Z_")&orderBy=title')
    },
    dataSourceRfqType () {
      return this.API('RFQTypes/GetAllDynamic?select=id,title')
    },
    orderPhaseClass () {
      return {
        'field-warn-extra': [20, 25].some((id) => id === this.form.orderPhaseID)
      }
    },
    classShipped () {
      return {
        'bg-green': this.form.shipped,
        'bg-orange': !this.form.shipped
      }
    },
    classQuoteReceived () {
      const { quoteReceived, quoteReceivedDate, shippedDate } = this.form
      const _date = quoteReceivedDate || new Date()

      const days = this.$moment(_date).diff(shippedDate, 'days')

      return {
        'bg-green': quoteReceived,
        'bg-orange': !quoteReceived && days >= 10
      }
    },
    classQuoteApproved () {
      const { quoteReceivedDate, quoteApprovedDate, quoteApproved } = this.form
      const _date = quoteApprovedDate || new Date()

      const days = this.$moment(_date).diff(quoteReceivedDate, 'days')

      return {
        'bg-green': quoteApproved,
        'bg-red': !quoteApproved && days >= 21,
        'bg-orange': !quoteApproved && days >= 7 && days < 21
      }
    },
    classUnitShipped () {
      return {
        'bg-green': this.form.unitShippedDate,
        'bg-none': !this.form.unitShippedDate
      }
    },
    classStandBy () {
      return {
        'bg-orange': this.form.standbyDate,
        'bg-none': !this.form.standbyDate
      }
    },
    classConfirmedEdd () {
      const { estimatedDeliveryDate } = this.form
      const dif = this.$moment(estimatedDeliveryDate).diff(new Date())
      return {
        'bg-red': dif < 0,
        'bg-orange': !estimatedDeliveryDate
      }
    }
  }
}
</script>
